import { DateInputLocalized } from '@components'

import { Flex, FormItem as FormItemDS, Icon, Input, Spacing, Text, Tooltip } from '@billy/design-system'
import { ReactElement, useCallback } from 'react'
import { useTranslation } from 'react-i18next'

import { useInvoiceEditorForm } from '../../../../../../contexts/invoiceEditorFormContext'
import { useInvoiceEditorDocumentType } from '../../../../../../hooks/useInvoiceEditorDocumentType'
import { useIsInvoiceEditorElementRestricted } from '../../../../../../hooks/useIsInvoiceEditorElementRestricted'
import { useUpdateDateOfSaleOrService } from '../../../../../../hooks/useUpdateDateOfSaleOrService'

export const InvoiceDateOfSaleOrService = (): ReactElement | null => {
  const { t } = useTranslation()
  const { isCreditNote, isRecurringInvoice } = useInvoiceEditorDocumentType()
  const { invoiceEditorForm, trackInvoiceEditor } = useInvoiceEditorForm()
  const isDisabled = useIsInvoiceEditorElementRestricted('dateOfSaleOrService', 'disabled')
  const isHidden = useIsInvoiceEditorElementRestricted('dateOfSaleOrService', 'hidden')

  const { FormItem, watch } = invoiceEditorForm
  const dateOfSaleOrService = watch('dateOfSaleOrService')

  useUpdateDateOfSaleOrService()

  const handleDatePickerOpen = useCallback(() => {
    trackInvoiceEditor('Form - Date of sale or service picker opened')
  }, [trackInvoiceEditor])

  if (isHidden || (isCreditNote && dateOfSaleOrService == null)) {
    return null
  }

  const label = (
    <Flex as="span" flexDirection="row" gap={Spacing.S6}>
      <Text weight="medium">{t('external_invoices.editor.form.field.date_of_sale_or_service.label')}</Text>
      <Tooltip
        as="span"
        label={t('external_invoices.editor.form.field.date_of_sale_or_service.tooltip')}
        placement="top"
      >
        <Icon icon="info" size="xs" />
      </Tooltip>
    </Flex>
  )

  return isRecurringInvoice ? (
    // just a visual placeholder; for this case we're sending a separate property "dateOfSaleOrServiceRule" in the payload
    <FormItemDS label={label}>
      <Input
        size="sm"
        disabled
        placeholder={t('external_invoices.editor.form.field.date_of_sale_or_service.recurring_invoice_setting')}
      />
    </FormItemDS>
  ) : (
    <FormItem
      name="dateOfSaleOrService"
      label={label}
      render={({ field: { onChange, ...props } }) => (
        <DateInputLocalized
          {...props}
          disabled={isDisabled}
          onChange={(date) => {
            onChange(date)
            trackInvoiceEditor('Form - Date of sale or service selected', {
              date,
            })
          }}
          onOpen={handleDatePickerOpen}
          size="sm"
        />
      )}
    />
  )
}
